import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { KpiLabelWithDescription } from '@/impactReports/KpiLabelWithDescription.atom';
import { SelectedProjectDisplayAndLink } from '@/impactReports/SelectedProjectDisplayAndLink.atom';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { UserNameCellRenderer } from '@/impactReports/cellRenderers/UserNameCellRenderer.atom';
import {
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  IMPACT_REPORT_LABEL_ID,
  REVIEWED_BY_FIRSTNAME,
  REVIEWED_BY_LASTNAME,
} from '@/impactReports/impactReport.types';
import { getImpactStageIcon, replaceLinks } from '@/impactReports/impactReport.utilities';
import {
  getCategoryDisplayString,
  getCustomCategoryTitle,
  getImpactTypeDisplayString,
  getKPIDisplayName,
  getKPIExpectedAnnualColumnName,
  getKPIUnit,
  getStageDisplayString,
} from '@/impactReports/impactReportDisplay.utilities';
import {
  useImpactReportActions,
  useImpactReportExpectedAnnualKpiVisibility,
  useImpactReportKpiColumns,
  useImpactReportTableId,
} from '@/impactReports/impactReports.store';
import { decorateMaterializedTableWithImpactValue, getValueFormatter } from '@/impactReports/tiles/table.utilities';
import { deleteTableRows } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import { Button, Icon } from '@seeqdev/qomponents';
import type { ICellRendererParams } from 'ag-grid-community';
import type { UserNameCellProps } from '@/impactReports/cellRenderers/UserNameCellRenderer.atom';
import type { ImpactReportCategory, ImpactReportUseCase, ImpactType, Stages } from '@/impactReports/impactReport.types';

interface ImpactReportDetailsProps extends ImpactReportUseCase {
  categories: ImpactReportCategory[] | undefined;
  [key: string]: any;
  onClose: () => void;
  onEdit?: () => void;
  editable?: boolean;
}

interface ColumnItem {
  title: ReactNode;
  value: string | undefined;
  expectedAnnual?: string | undefined;
}

export const ImpactReportDetailHeader = (props: ImpactReportUseCase) => {
  const assignedWorkbookId = props[SeeqNames.ImpactReport.AssignedToWorkbook];
  const assignedWorksheetId = props[SeeqNames.ImpactReport.AssignedToWorksheet];
  const isProject = props[SeeqNames.ImpactReport.ImpactBasedOn] === SeeqNames.Types.Project;
  const params = {
    value: {
      first: props[IMPACT_OWNER_FIRSTNAME]!,
      last: props[IMPACT_OWNER_LASTNAME]!,
    },
  } as ICellRendererParams<any, UserNameCellProps>;
  const location = props[SeeqNames.ImpactReport.Location] || null;
  const locationObj = location !== null ? JSON.parse(location) : null;
  const locationDisplay = locationObj ? locationObj.label : undefined;
  const site = props[SeeqNames.ImpactReport.ImpactSite] || undefined;
  const locationAndSite = _.compact([locationDisplay, site]).join(', ');
  return (
    <div className="tw:flex tw:w-full tw:flex-col">
      <div className="tw:mb-2 tw:flex tw:flex-1 tw:items-center tw:justify-between tw:overflow-hidden">
        <div className="tw:flex tw:flex-col">
          <UserNameCellRenderer {...params} />
        </div>

        <div className="tw:flex tw:flex-row tw:items-end tw:rounded-lg tw:border tw:border-sq-disabled-gray tw:p-2.5">
          <Icon
            type="theme"
            testId="stageIcon"
            icon={getImpactStageIcon(props[SeeqNames.ImpactReport.ImpactStatus] as Stages)}
            extraClassNames="tw:mr-5px tw:mb-3px"
          />
          {getStageDisplayString(props[SeeqNames.ImpactReport.ImpactStatus] as Stages)}
        </div>
      </div>
      <h2 className="tw:mb-2.5">{props[SeeqNames.ImpactReport.ImpactName]}</h2>
      {locationAndSite}
      <br />
      {isProject && assignedWorkbookId ? (
        <SelectedProjectDisplayAndLink projectId={assignedWorkbookId} />
      ) : assignedWorkbookId && assignedWorksheetId ? (
        <SelectedWorksheetDisplayAndLink worksheetId={assignedWorksheetId} workbookId={assignedWorkbookId} />
      ) : null}
    </div>
  );
};
export const ImpactReportDetail = (props: ImpactReportDetailsProps) => {
  const { t } = useTranslation();
  const { setForceTableReload } = useImpactReportActions();
  const { categories, onClose, onEdit } = props;
  const tableId = useImpactReportTableId();
  const kpiColumns = useImpactReportKpiColumns();
  const expectedAnnualKpiVisibility = useImpactReportExpectedAnnualKpiVisibility();
  const [kpiDisplays, setKpiDisplays] = useState<ColumnItem[]>([]);

  const deleteUseCase = async () => {
    try {
      const itemId = props[SeeqNames.MaterializedTables.ItemIdColumn];
      if (!itemId || !tableId) {
        throw new Error('Missing ids required to delete impact report');
      }
      const contextLabelIds =
        categories
          ?.map((category) => {
            const categoryId = category.category.id;
            return categoryId ? props[`${categoryId}_${IMPACT_REPORT_LABEL_ID}`] : undefined;
          })
          .filter((contextLabelId): contextLabelId is string => Boolean(contextLabelId)) ?? [];

      await Promise.all(
        contextLabelIds.map((contextLabelId) => sqContextApi.deleteContextLabel({ itemId, labelId: contextLabelId })),
      );

      await deleteTableRows(tableId, [
        {
          itemId,
          datumId: props[SeeqNames.MaterializedTables.DatumIdColumn],
        },
      ]);
      setForceTableReload(true);
      onClose();
    } catch (e) {
      errorToast({ messageKey: 'IMPACT_REPORT.NO_DELETE' });
    }
  };

  useAsyncEffect(async () => {
    const data = await decorateMaterializedTableWithImpactValue(
      [props],
      kpiColumns ?? [],
      moment().subtract(1, 'year').toISOString(),
      moment().toISOString(),
    );
    const kpiWithValue = kpiColumns.map((item) => {
      const unit = getKPIUnit(item.columnName);
      const formatterFn = getValueFormatter(unit);
      const value = data[0][item.columnName];
      const expectedAnnualColumnName = getKPIExpectedAnnualColumnName(item.columnName);
      const expectedAnnualRaw = props[expectedAnnualColumnName];
      const expectedAnnualNumber = Number(expectedAnnualRaw);
      const expectedAnnualDisplay =
        expectedAnnualRaw === undefined || expectedAnnualRaw === null || expectedAnnualRaw === ''
          ? '-'
          : Number.isFinite(expectedAnnualNumber)
            ? formatterFn(expectedAnnualNumber, unit)
            : String(expectedAnnualRaw);
      return {
        title: <KpiLabelWithDescription label={getKPIDisplayName(item.columnName)} description={item.description} />,
        value: !value ? '-' : formatterFn(value, unit),
        expectedAnnual: expectedAnnualKpiVisibility[item.columnName] === true ? expectedAnnualDisplay : undefined,
      };
    });
    setKpiDisplays(kpiWithValue ?? []);
  }, [props, kpiColumns, expectedAnnualKpiVisibility]);

  const categoryDisplays =
    categories?.map((item) => {
      const display =
        (item.category?.id && getCategoryDisplayString(props[item.category.id], item.options)) ?? undefined;
      return { title: getCustomCategoryTitle(item), value: display };
    }) ?? [];

  const reviewedByName = `${props[REVIEWED_BY_FIRSTNAME] ?? ''} ${props[REVIEWED_BY_LASTNAME] ?? ''}`.trim();
  const reviewDate = props[SeeqNames.ImpactReport.ReviewDate];
  const reviewDisplays = [
    props[SeeqNames.ImpactReport.ReviewComment]
      ? {
          title: t('IMPACT_REPORT.REVIEW_COMMENT'),
          value: props[SeeqNames.ImpactReport.ReviewComment],
        }
      : null,
    reviewDate
      ? {
          title: t('IMPACT_REPORT.REVIEW_DATE'),
          value: moment(reviewDate).format('l'),
        }
      : null,
    reviewedByName || props[SeeqNames.ImpactReport.ReviewedBy]
      ? {
          title: t('IMPACT_REPORT.REVIEWED_BY'),
          value: reviewedByName || props[SeeqNames.ImpactReport.ReviewedBy],
        }
      : null,
  ].filter(Boolean) as ColumnItem[];

  const columns = [
    {
      title: t('IMPACT_REPORT.TABLE_HEADERS.TYPE'),
      value: getImpactTypeDisplayString(props[SeeqNames.ImpactReport.ImpactSavingsType] as ImpactType),
    },
    ...categoryDisplays,
    ...reviewDisplays,
  ];

  return (
    <div className="tw:h-full tw:overflow-y-auto tw:p-[25px]">
      <div className="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden">
        <div className="tw:mb-2.5 tw:flex tw:flex-col">
          <ContainerWithHTML
            content={replaceLinks(
              props[SeeqNames.ImpactReport.ImpactDescription] ?? '',
              props[SeeqNames.ImpactReport.AssignedToWorkbook],
              props[SeeqNames.ImpactReport.AssignedToWorksheet],
            )}
            isBlock
          />
        </div>

        <div className="tw:ml-2.5 tw:w-full">
          <h4>{t('IMPACT_REPORT.TOTAL_IMPACT_PAST_YEAR')}</h4>
          {kpiDisplays
            ?.reduce<ColumnItem[][]>((rows, item, index) => {
              if (index % 2 === 0) {
                rows.push(kpiDisplays?.slice(index, index + 2));
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div className="tw:flex tw:flex-wrap tw:gap-x-6" key={rowIndex}>
                {row.map((item, colIndex) => {
                  return (
                    <div className="tw:min-w-0 tw:flex-1 tw:pb-2.5" key={colIndex}>
                      <div className="tw:mb-px tw:text-lg">{item.title}</div>
                      <div className="tw:mb-0">{item.value}</div>
                      {item.expectedAnnual !== undefined && (
                        <div className="tw:mt-0.5 tw:text-sm tw:text-sq-text-muted">
                          {t('IMPACT_REPORT.EXPECTED_ANNUAL_SAVINGS')}: {item.expectedAnnual}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
        <hr className="tw:mb-1.5" />
        <div className="tw:ml-2.5 tw:w-full">
          {columns
            ?.reduce<ColumnItem[][]>((rows, item, index) => {
              if (index % 2 === 0) {
                rows.push(columns?.slice(index, index + 2));
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div className="tw:flex tw:flex-wrap tw:gap-x-6" key={rowIndex}>
                {row.map((item, colIndex) => {
                  return (
                    <div className="tw:min-w-0 tw:flex-1" key={colIndex}>
                      <div className="tw:mb-px tw:text-lg">{item.title}</div>
                      <p>{item.value}</p>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>

      <div className="tw:mt-[25px] tw:flex tw:flex-1 tw:flex-row tw:overflow-hidden">
        <div className="tw:flex tw:flex-1 tw:flex-row tw:overflow-hidden">
          {onEdit && <Button label={t('DELETE')} onClick={deleteUseCase} variant="danger" testId="deleteImpact" />}
        </div>
        <Button testId="closeImpactDetail" label={t('CLOSE')} onClick={onClose} extraClassNames="tw:mr-2.5" />
        {onEdit && <Button label={t('EDIT')} onClick={onEdit} variant="theme" testId="editImpact" />}
      </div>
    </div>
  );
};
